import React from 'react';
import { Card } from 'antd';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import type { PlayCountDistribution } from '../../../../stores/labatt-game';
import * as Colors from '../../../../styles/colors';
import style from '../../style.module.scss';

interface PlayCountDistributionChartProps {
    playCountDistribution: PlayCountDistribution[];
}

interface ChartDataItem {
    gamesPlayed: number;
    userCount: number;
    label: string;
}

const formatPlayCountDistributionForChart = (
    playCountDistribution: PlayCountDistribution[]
): ChartDataItem[] => {
    return playCountDistribution
        .slice()
        .sort((a, b) => a.gamesPlayed - b.gamesPlayed)
        .map((item) => ({
            gamesPlayed: item.gamesPlayed,
            userCount: item.userCount,
            label:
                item.gamesPlayed === 1 ? '1 Game' : `${item.gamesPlayed} Games`,
        }));
};

const formatTooltipValue = (
    value: number | string | readonly (number | string)[] | undefined
): React.ReactNode => {
    const numValue = Number(Array.isArray(value) ? value[0] : value);
    return `${numValue} user${numValue !== 1 ? 's' : ''}`;
};

const formatTooltipLabel = (label: React.ReactNode): React.ReactNode => {
    return `Games Played: ${label}`;
};

export const PlayCountDistributionChart: React.FC<PlayCountDistributionChartProps> = ({
    playCountDistribution,
}) => {
    const chartData = formatPlayCountDistributionForChart(
        playCountDistribution
    );

    return (
        <div className={style.section_spacing}>
            <h2 className={style.section_title}>Play Count Distribution</h2>
            <Card className={style.chart_card}>
                <ResponsiveContainer width="100%" height={400}>
                    <LineChart
                        data={chartData}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 20,
                        }}
                    >
                        <CartesianGrid
                            strokeDasharray="4 4"
                            stroke={Colors.UNKNOWN_CT_BLUE_OPACITY_15}
                        />
                        <XAxis
                            dataKey="label"
                            tick={{
                                fontSize: 12,
                            }}
                            label={{
                                value: 'Number of Games Played',
                                position: 'insideBottom',
                                offset: -5,
                                style: {
                                    textAnchor: 'middle',
                                },
                            }}
                        />
                        <YAxis
                            tick={{
                                fontSize: 12,
                            }}
                            allowDecimals={false}
                            domain={[0, 'dataMax + 1']}
                            label={{
                                value: 'Number of Users',
                                angle: -90,
                                position: 'insideLeft',
                                style: {
                                    textAnchor: 'middle',
                                },
                            }}
                        />
                        <Tooltip
                            formatter={formatTooltipValue}
                            labelFormatter={formatTooltipLabel}
                        />
                        <Line
                            type="monotone"
                            dataKey="userCount"
                            stroke={Colors.UNKNOWN_CT_BLUE}
                            strokeWidth={3}
                            dot={{
                                r: 6,
                                fill: Colors.UNKNOWN_CT_BLUE,
                                strokeWidth: 2,
                                stroke: Colors.WHITE,
                            }}
                            activeDot={{
                                r: 8,
                            }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </Card>
        </div>
    );
};
